/**
 * Stok Listesi — her ingredient × location için quantity + low_threshold
 * + eşik edit modal (Sprint 33).
 */
import {
  ActionIcon, Badge, Button, Card, Group, Modal,
  NumberInput, SegmentedControl, Stack, Text, TextInput, Tooltip,
} from "@mantine/core";
import { IconAlertTriangle, IconEdit, IconSearch } from "@tabler/icons-react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, useInfiniteResource } from "../../../shared/api";
import {
  PageHeader, EmptyState, InfiniteTable, MoneyDisplay, PriceHistoryDrawer, formatQty,
} from "../../../shared/ui";

// formatQty artık shared/ui/format.ts'ten geliyor — TR locale (örn. "1,5")
// + tam sayılarda ondalık göstermez ("5000.000" → "5.000" thousand separator).

export function StockItemsPage() {
  // Arama (server-side, debounced) + tip filtresi (Tümü/Hammadde/Ürün)
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300);
  const [kind, setKind] = useState<"" | "INGREDIENT" | "PRODUCT">("");
  // Başka sayfadan navigasyonla preset uygulandı mı? Kullanıcı arama
  // kutusunu boşaltırsa preset'in `kind` kısmı da sıfırlanır — yoksa
  // "Cola sildim ama ürünler filtresi takılı" sürprizi olur.
  const [presetActive, setPresetActive] = useState(false);

  // Başka sayfadan (örn. Kritik Stok) navigasyonla gelen parametreler:
  // search ve/veya kind set edilir, sonra sessionStorage temizlenir.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin:nav:stock-items");
      if (!raw) return;
      const params = JSON.parse(raw);
      sessionStorage.removeItem("admin:nav:stock-items");
      let applied = false;
      if (typeof params.search === "string") {
        setSearch(params.search);
        applied = true;
      }
      if (params.kind === "INGREDIENT" || params.kind === "PRODUCT") {
        setKind(params.kind);
        applied = true;
      }
      if (applied) setPresetActive(true);
    } catch { /* ignore */ }
  }, []);

  // Preset aktifken kullanıcı search'i temizlerse kind filtresini de bırak
  // — beklenti: "tüm liste"ye dön. Manuel `kind` değişimi presetden çıkış
  // anlamına gelir; sonraki search temizlemeleri etkilemez.
  useEffect(() => {
    if (presetActive && debouncedSearch === "") {
      setKind("");
      setPresetActive(false);
    }
  }, [debouncedSearch, presetActive]);

  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("stock-items", {
    search: debouncedSearch,
    ...(kind ? { filters: { kind } } : {}),
  });
  const [editing, setEditing] = useState<any | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  // Birim Maliyet hücresine tıklanınca açılan "son alış fiyatları" drawer'ı.
  const [historyItem, setHistoryItem] = useState<any | null>(null);

  const handleEdit = (item: any) => {
    setEditing(item);
    open();
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Stok"
        subtitle="Malzeme ve ürün stok seviyeleri — kritik eşik (her kalem × lokasyon için ayrı)"
      />
      <Group gap="sm" align="flex-end">
        <TextInput
          placeholder="Hammadde / ürün adı veya SKU ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <SegmentedControl
          value={kind}
          onChange={(v) => {
            setKind(v as "" | "INGREDIENT" | "PRODUCT");
            setPresetActive(false);  // manuel seçim → preset bağı kopar
          }}
          data={[
            { label: "Tümü", value: "" },
            { label: "Hammadde", value: "INGREDIENT" },
            { label: "Ürün", value: "PRODUCT" },
          ]}
        />
      </Group>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(s) => s.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          striped
          emptyContent={
            <EmptyState
              title={debouncedSearch || kind ? "Eşleşen stok kaydı yok" : "Stok kaydı yok"}
            />
          }
          columns={[
            {
              header: "Kalem",
              render: (s) => (
                <Group gap={6} wrap="nowrap">
                  <Text fw={500} span>
                    {s.product_name || s.ingredient_name || s.product || s.ingredient}
                  </Text>
                  {/* Stok kalemi hammadde mi ürün mü — kind serializer'dan gelir */}
                  {s.kind === "PRODUCT"
                    ? <Badge size="xs" variant="light" color="violet">Ürün</Badge>
                    : <Badge size="xs" variant="light" color="blue">Hammadde</Badge>}
                </Group>
              ),
            },
            { header: "Lokasyon", render: (s) => s.location_name || s.location },
            {
              header: "Miktar",
              align: "right",
              render: (s) => {
                const qty = Number(s.quantity);
                const thr = Number(s.low_threshold);
                const isOut = qty <= 0;
                const isCritical = thr > 0 && qty > 0 && qty <= thr;
                return (
                  <Text fw={700} span {...(isOut ? { c: "red" } : (isCritical ? { c: "orange" } : {}))}>
                    {formatQty(s.quantity)}
                    <Text span size="xs" c="dimmed" fw={400}> {s.unit}</Text>
                  </Text>
                );
              },
            },
            {
              header: "Kritik Eşik",
              align: "right",
              render: (s) => (
                <>
                  {formatQty(s.low_threshold)}
                  <Text span size="xs" c="dimmed"> {s.unit}</Text>
                </>
              ),
            },
            {
              header: "Birim Maliyet",
              align: "right",
              // Hammadde: AVCO ortalama maliyet (mal kabulde otomatik güncellenir).
              // Ürün: son alış maliyeti. 0 = "—" (sahte maliyet gösterme).
              // Tıklanınca son 5 alış fiyatı (mal kabul geçmişi) drawer'ı açılır.
              render: (s) => {
                const cost = Number(s.unit_cost_minor) || 0;
                if (cost <= 0) {
                  return (
                    <Tooltip label="Maliyet henüz oluşmadı — mal kabul (alım) yapıldığında dolar">
                      <Text size="xs" c="dimmed">—</Text>
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip label="Son alış fiyatlarını gör">
                    <Group
                      gap={4}
                      justify="flex-end"
                      style={{ cursor: "pointer" }}
                      onClick={() => setHistoryItem(s)}
                    >
                      <MoneyDisplay minor={cost} fw={600} />
                      <Text span size="xs" c="dimmed">/ {s.unit}</Text>
                    </Group>
                  </Tooltip>
                );
              },
            },
            {
              header: "Durum",
              render: (s) => {
                const qty = Number(s.quantity);
                const thr = Number(s.low_threshold);
                const isOut = qty <= 0;
                const isCritical = thr > 0 && qty > 0 && qty <= thr;
                return isOut ? (
                  <Badge color="red" leftSection={<IconAlertTriangle size={12} />}>
                    Tükendi
                  </Badge>
                ) : isCritical ? (
                  <Badge color="orange" leftSection={<IconAlertTriangle size={12} />}>
                    Kritik
                  </Badge>
                ) : (
                  <Badge color="green" variant="light">Normal</Badge>
                );
              },
            },
            {
              header: "",
              render: (s) => (
                <Tooltip label="Kritik eşik düzenle">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => handleEdit(s)}
                    aria-label="Eşik düzenle"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              ),
            },
          ]}
        />
      </Card>

      <LowThresholdModal
        opened={opened}
        item={editing}
        onClose={() => { close(); setEditing(null); }}
      />

      <PriceHistoryDrawer
        opened={!!historyItem}
        onClose={() => setHistoryItem(null)}
        title={historyItem?.product_name || historyItem?.ingredient_name || "Kalem"}
        endpoint={
          historyItem
            ? `/api/v1/stock-items/${historyItem.id}/price-history/`
            : null
        }
        unit={historyItem?.unit}
      />
    </Stack>
  );
}

function LowThresholdModal({
  opened, item, onClose,
}: { opened: boolean; item: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState<number | string>(0);

  // Modal başka bir item için açılınca eşik değerini senkronla.
  // (Önceki kod useState initializer kullanıyordu — item değişince
  // çalışmaz; doğrusu useEffect.)
  useEffect(() => {
    if (item) setValue(Number(item.low_threshold || 0));
  }, [item]);

  const mutation = useMutation({
    mutationFn: async (newThreshold: number) =>
      apiClient.patch(`/api/v1/stock-items/${item.id}/`, {
        low_threshold: String(newThreshold),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-items"] });
      qc.invalidateQueries({ queryKey: ["stock-alerts"] });
      notifications.show({
        message: `${item.product_name || item.ingredient_name || "Stok"} eşiği güncellendi`,
        color: "green",
      });
      onClose();
    },
    onError: (e: any) => {
      notifications.show({
        message: e?.response?.data?.detail || "Hata",
        color: "red",
      });
    },
  });

  if (!item) return null;

  return (
    <Modal opened={opened} onClose={onClose} title={`Kritik Eşik: ${item.product_name || item.ingredient_name || ""}`} size="md">
      <Stack>
        <Text size="sm" c="dimmed">
          Stok bu seviyenin altına düştüğünde sahip + yöneticilere otomatik
          bildirim gider (Dashboard widget + ayarlı kanallar). Her ingredient
          × lokasyon için ayrı eşik tanımlanır.
        </Text>
        <Text size="sm">
          <strong>Mevcut miktar:</strong> {item.quantity}
          {" · "}
          <strong>Lokasyon:</strong> {item.location_name}
        </Text>
        <NumberInput
          label="Kritik Eşik (alttına düşünce alarm)"
          description="0 = alarm pasif"
          value={value}
          onChange={setValue}
          decimalScale={3}
          min={0}
          step={0.5}
          suffix={item.unit ? ` ${item.unit}` : ""}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button
            onClick={() => mutation.mutate(Number(value))}
            loading={mutation.isPending}
          >
            Kaydet
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
